/* ------------------------------------------------------------------
   EmailMarketingPage.js — Marketing → Email Marketing
   List of campaigns + a composer (subject / from / audience from CRM /
   body) that sends through the backend (Amazon SES). Shows monthly quota.
------------------------------------------------------------------ */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import EmailBlockEditor, { renderBlocksToHtml } from './email/EmailBlockEditor';

const ax = axios.create({ withCredentials: true });

const STATUS_BADGE = {
  draft:    { label: 'Draft',    bg: '#f3f4f6', color: '#374151' },
  scheduled:{ label: 'Scheduled',bg: '#dbeafe', color: '#1e40af' },
  sending:  { label: 'Sending…', bg: '#fef9c3', color: '#854d0e' },
  sent:     { label: 'Sent',     bg: '#dcfce7', color: '#166534' },
  failed:   { label: 'Failed',   bg: '#fef2f2', color: '#dc2626' },
};

const blankDraft = () => ({ id: null, subject: '', fromName: '', replyTo: '', bodyHtml: '', bodyBlocks: [], editorMode: 'design', audience: { listId: '', status: '', source: '', tag: '' } });

export default function EmailMarketingPage() {
  const [view, setView]         = useState('list');     // 'list' | 'compose'
  const [statsFor, setStatsFor] = useState(null);       // sent campaign whose report is open
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists]       = useState([]);
  const [defaults, setDefaults] = useState({ fromName: '', replyTo: '' });
  const [usage, setUsage]       = useState({ used: 0, limit: 0, left: 0 });
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState(null);

  const [draft, setDraft]       = useState(blankDraft());
  const [audienceCount, setAudienceCount] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [sending, setSending]   = useState(false);
  const [confirm, setConfirm]   = useState(null);   // { title, message, confirmLabel, danger, onConfirm }
  const [userId, setUserId]     = useState(null);   // for image uploads
  const [previewOpen, setPreviewOpen] = useState(false);
  const debounceRef = useRef(null);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, u, l, s] = await Promise.all([ax.get('/api/email/campaigns'), ax.get('/api/email/usage'), ax.get('/api/email/lists'), ax.get('/api/email/settings')]);
      setCampaigns(c.data.campaigns || []);
      setUsage(u.data || { used: 0, limit: 0, left: 0 });
      setLists(l.data.lists || []);
      setDefaults(s.data.settings || { fromName: '', replyTo: '' });
      try { const me = await ax.get('/api/auth/me'); if (me.data?.user?.id) setUserId(me.data.user.id); } catch { /* ignore */ }
    } catch (e) { console.error('load email', e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  /* live recipient count for the chosen audience */
  useEffect(() => {
    if (view !== 'compose') return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { listId, status, source, tag } = draft.audience;
        const { data } = await ax.get('/api/email/audience-count', { params: { listId, status, source, tag } });
        setAudienceCount(data.count);
      } catch { setAudienceCount(null); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [draft.audience, view]);

  // From name / reply-to come from Settings at send time (left blank on the draft)
  function newCampaign() { setDraft(blankDraft()); setAudienceCount(null); setView('compose'); }
  async function editCampaign(c) {
    if (c.status !== 'draft') { flash('Only drafts can be edited.', false); return; }
    try {
      const { data } = await ax.get(`/api/email/campaigns/${c.id}`);
      const p = data.campaign;
      const blocks = Array.isArray(p.body_blocks) ? p.body_blocks : [];
      setDraft({ id: p.id, subject: p.subject || '', fromName: p.from_name || '', replyTo: p.reply_to || '',
        bodyHtml: p.body_html || '', bodyBlocks: blocks,
        editorMode: blocks.length ? 'design' : (p.body_html ? 'html' : 'design'),
        audience: { listId: '', status: '', source: '', tag: '', ...(p.audience || {}) } });
      setView('compose');
    } catch { flash('Could not open campaign', false); }
  }

  // the HTML that actually gets sent: rendered from blocks in design mode, else the raw HTML
  const composedHtml = () => draft.editorMode === 'design' ? renderBlocksToHtml(draft.bodyBlocks) : (draft.bodyHtml || '');

  async function saveDraft(silent = false) {
    const body = {
      subject: draft.subject, fromName: draft.fromName, replyTo: draft.replyTo,
      bodyHtml: composedHtml(),
      bodyBlocks: draft.editorMode === 'design' ? draft.bodyBlocks : null,
      audience: cleanAudience(draft.audience),
    };
    const res = draft.id
      ? await ax.put(`/api/email/campaigns/${draft.id}`, body)
      : await ax.post('/api/email/campaigns', body);
    const saved = res.data.campaign;
    setDraft(d => ({ ...d, id: saved.id }));
    if (!silent) flash('Draft saved');
    return saved;
  }
  async function onSaveClick() {
    if (!draft.subject) { flash('Add a subject first.', false); return; }
    setSaving(true);
    try { await saveDraft(); load(); } catch (e) { flash(e.response?.data?.error || 'Save failed', false); }
    finally { setSaving(false); }
  }

  const sendingPaused = !!usage.sendingPaused;
  // sender identity comes from Settings (not re-entered per campaign)
  const senderAddr  = (defaults.senderMode === 'domain' && defaults.fromEmailStatus === 'verified') ? defaults.fromEmail : defaults.sendingAddress;
  const senderName  = defaults.fromName || '';
  const senderReply = defaults.replyTo || defaults.accountEmail || '';

  function onSend() {
    if (!draft.subject) { flash('Add a subject first.', false); return; }
    if (sendingPaused) { flash('Email sending is paused on your account. Contact support.', false); return; }
    if (!audienceCount) { flash('No recipients match this audience.', false); return; }
    setConfirm({
      title: 'Send campaign',
      message: `“${draft.subject}” will be sent to ${audienceCount.toLocaleString()} contact${audienceCount === 1 ? '' : 's'}. This can’t be undone.`,
      confirmLabel: `Send to ${audienceCount.toLocaleString()}`,
      onConfirm: doSend,
    });
  }

  async function doSend() {
    setConfirm(null);
    setSending(true);
    try {
      const saved = await saveDraft(true);
      const { data } = await ax.post(`/api/email/campaigns/${saved.id}/send`);
      flash(`Sending to ${data.recipientCount} contact${data.recipientCount === 1 ? '' : 's'}…`);
      setView('list');
      load();
      setTimeout(load, 4000); // refresh once the background send progresses
    } catch (e) {
      flash(e.response?.data?.error || 'Send failed', false);
    } finally { setSending(false); }
  }

  function deleteCampaign(c) {
    setConfirm({
      title: 'Delete campaign',
      message: `Delete “${c.subject}”? This can’t be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => { setConfirm(null); try { await ax.delete(`/api/email/campaigns/${c.id}`); load(); } catch { flash('Delete failed', false); } },
    });
  }

  const setAud = (patch) => setDraft(d => ({ ...d, audience: { ...d.audience, ...patch } }));

  /* ───────── COMPOSE VIEW ───────── */
  if (view === 'compose') {
    const designMode = draft.editorMode === 'design';
    return (
      <div style={{ maxWidth: designMode ? 1200 : 1000 }}>
        {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
        {previewOpen && <PreviewModal subject={draft.subject} fromName={senderName || draft.fromName || 'Greatly Social'} fromEmail={senderAddr} html={composedHtml()} onClose={() => setPreviewOpen(false)} />}
        <button onClick={() => { setView('list'); load(); }} style={{ ...ghostBtn, marginBottom: 14 }}>← Back to campaigns</button>
        {msg && <Banner msg={msg} />}
        {sendingPaused && (
          <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13.5, fontWeight: 600,
            background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
            Email sending is paused on your account{usage.pausedReason ? ` — ${usage.pausedReason}` : ''}. Contact support to re-enable.
          </div>
        )}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* editor */}
          <div style={{ flex: '1 1 440px', minWidth: 320, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card title="Campaign">
              <Label>Subject</Label>
              <input value={draft.subject} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))} placeholder="e.g. October newsletter" style={inputStyle} />

              {/* sender identity — pulled from Settings, not re-entered here */}
              <div style={{ marginTop: 14, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Sending as</div>
                {(senderName || senderAddr) ? (
                  <>
                    <div style={{ fontSize: 14, color: '#111827' }}>
                      <strong>{senderName || <span style={{ color: '#dc2626' }}>Set a from name</span>}</strong>
                      {senderAddr && <span style={{ color: '#6b7280' }}> &lt;{senderAddr}&gt;</span>}
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Replies go to {senderReply || '—'}</div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: '#6b7280' }}>No sender configured yet.</div>
                )}
                <Link to="/dashboard/marketing/email/settings" style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, textDecoration: 'none', display: 'inline-block', marginTop: 8 }}>Change in Settings →</Link>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, marginBottom: 8 }}>
                <Label style={{ margin: 0 }}>Body</Label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button type="button" onClick={() => setPreviewOpen(true)} style={{ ...ghostBtn, padding: '5px 12px', fontSize: 12.5 }}>👁 Preview</button>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" onClick={() => setDraft(d => ({ ...d, editorMode: 'design' }))} style={toggleBtn(draft.editorMode === 'design')}>Design</button>
                    <button type="button" onClick={() => setDraft(d => ({ ...d, editorMode: 'html' }))} style={toggleBtn(draft.editorMode === 'html')}>HTML</button>
                  </div>
                </div>
              </div>
              {draft.editorMode === 'design' ? (
                <EmailBlockEditor blocks={draft.bodyBlocks} onChange={(blocks) => setDraft(d => ({ ...d, bodyBlocks: blocks }))} userId={userId} />
              ) : (
                <textarea value={draft.bodyHtml} onChange={e => setDraft(d => ({ ...d, bodyHtml: e.target.value }))} rows={10}
                  placeholder="<h1>Hi there!</h1><p>Write your email…</p>" style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }} />
              )}
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>An unsubscribe link is added automatically (required for compliance).</div>
              {usage.branding && (
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>A small “Sent with Greatly Social” footer is added on your plan. <span style={{ color: '#2563eb', fontWeight: 600 }}>Upgrade to remove it.</span></div>
              )}
            </Card>

            <Card title="Audience">
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Pick who receives this from your CRM. Unsubscribed contacts are always excluded.</div>
              <div style={{ marginBottom: 12 }}>
                <Label>List</Label>
                <select value={draft.audience.listId} onChange={e => setAud({ listId: e.target.value })} style={{ ...selectStyle, width: '100%', minWidth: 0 }}>
                  <option value="">Everyone in CRM (no list)</option>
                  {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.member_count})</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <Label>Status</Label>
                  <select value={draft.audience.status} onChange={e => setAud({ status: e.target.value })} style={selectStyle}>
                    <option value="">All</option><option value="lead">Leads</option><option value="subscriber">Subscribers</option><option value="customer">Customers</option>
                  </select>
                </div>
                <div>
                  <Label>Source</Label>
                  <select value={draft.audience.source} onChange={e => setAud({ source: e.target.value })} style={selectStyle}>
                    <option value="">All</option><option value="link-in-bio">Link in Bio</option><option value="manual">Manual</option><option value="import">Imported</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <Label>Tag</Label>
                  <input value={draft.audience.tag} onChange={e => setAud({ tag: e.target.value })} placeholder="(optional)" style={inputStyle} />
                </div>
              </div>
              <div style={{ marginTop: 14, fontSize: 14, color: '#111827' }}>
                Recipients: <strong>{audienceCount == null ? '…' : audienceCount.toLocaleString()}</strong>
                <span style={{ color: '#6b7280' }}> · {usage.left.toLocaleString()} of your monthly quota left</span>
              </div>
            </Card>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onSaveClick} disabled={saving} style={{ ...ghostBtn, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save draft'}</button>
              <button onClick={onSend} disabled={sending || !audienceCount || sendingPaused} title={sendingPaused ? 'Sending is paused on your account' : undefined} style={{ ...primaryBtn, opacity: (sending || !audienceCount || sendingPaused) ? 0.6 : 1 }}>{sending ? 'Sending…' : `Send${audienceCount ? ` to ${audienceCount}` : ''}`}</button>
            </div>
          </div>

          {/* preview — only in HTML mode; in Design mode the canvas IS the preview */}
          {!designMode && (
            <div style={{ flex: '0 0 360px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 10 }}>Preview</div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{draft.subject || '(no subject)'}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>From: {senderName || draft.fromName || 'Greatly Social'}{senderAddr ? ` <${senderAddr}>` : ''}</div>
                </div>
                <div style={{ padding: 16, fontSize: 14, color: '#111827', minHeight: 120 }}
                  dangerouslySetInnerHTML={{ __html: composedHtml() || '<p style="color:#9ca3af">Your email preview appears here…</p>' }} />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ───────── LIST VIEW ───────── */
  return (
    <div>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Email Marketing</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Send campaigns to your CRM contacts.</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {usage.limit > 0 && (
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              <strong style={{ color: '#111827' }}>{usage.left.toLocaleString()}</strong> / {usage.limit.toLocaleString()} emails left this month
            </div>
          )}
          <button onClick={newCampaign} style={primaryBtn}>+ New campaign</button>
        </div>
      </div>

      {msg && <Banner msg={msg} />}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : campaigns.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
            No campaigns yet. Click <strong>New campaign</strong> to send your first email.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', color: '#6b7280', textAlign: 'left' }}>
                <Th>Subject</Th><Th>Status</Th><Th>Recipients</Th><Th>Sent</Th><Th>Date</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => {
                const sb = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <Td><span style={{ fontWeight: 600, color: '#111827' }}>{c.subject}</span></Td>
                    <Td><span style={{ fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 99, background: sb.bg, color: sb.color }}>{sb.label}</span></Td>
                    <Td>{c.recipient_count || 0}</Td>
                    <Td>{c.sent_count || 0}{c.failed_count ? <span style={{ color: '#dc2626' }}> ({c.failed_count} failed)</span> : ''}</Td>
                    <Td><span style={{ color: '#6b7280' }}>{String(c.sent_at || c.created_at || '').slice(0, 10)}</span></Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {c.status === 'sent' && <button onClick={() => setStatsFor(c)} style={{ ...miniBtn, color: '#2563eb', borderColor: '#bfdbfe' }}>Report</button>}
                        {c.status === 'draft' && <button onClick={() => editCampaign(c)} style={miniBtn}>Edit</button>}
                        {c.status !== 'sending' && <button onClick={() => deleteCampaign(c)} style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca' }}>Delete</button>}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {statsFor && <StatsModal campaign={statsFor} onClose={() => setStatsFor(null)} />}
    </div>
  );
}

/* ===================== campaign report modal ===================== */
function StatsModal({ campaign, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(false);

  useEffect(() => {
    let alive = true;
    ax.get(`/api/email/campaigns/${campaign.id}/stats`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [campaign.id]);

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: 16 };
  const modal   = { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Campaign report</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{campaign.subject}</div>
          </div>
          <button onClick={onClose} style={{ ...miniBtn, border: 'none' }}>✕</button>
        </div>

        <div style={{ padding: 20 }}>
          {err ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>Could not load report.</div>
          ) : !data ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
                <Metric label="Open rate" value={`${data.opens.rate}%`} sub={`${data.opens.unique.toLocaleString()} of ${(data.delivered || data.sent).toLocaleString()} opened`} accent="#2563eb" big />
                <Metric label="Click rate" value={`${data.clicks.rate}%`} sub={`${data.clicks.unique.toLocaleString()} clicked · ${data.clickToOpen}% of openers`} accent="#7c3aed" big />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <Metric label="Sent" value={data.sent.toLocaleString()} />
                <Metric label="Delivered" value={data.deliveryTracking ? data.delivered.toLocaleString() : '—'} />
                <Metric label="Total opens" value={data.opens.total.toLocaleString()} />
                <Metric label="Total clicks" value={data.clicks.total.toLocaleString()} />
                <Metric label="Bounces" value={data.bounces.toLocaleString()} danger={data.bounces > 0} />
                <Metric label="Complaints" value={data.complaints.toLocaleString()} danger={data.complaints > 0} />
              </div>
              <p style={{ fontSize: 11.5, color: '#9ca3af', margin: '16px 0 0', lineHeight: 1.5 }}>
                Opens are tracked via an invisible pixel and are approximate — some inboxes (e.g. Apple Mail Privacy) pre-load images, and some block them entirely.
                {!data.deliveryTracking && ' Rates are based on emails sent (SES delivery events not enabled).'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Metric = ({ label, value, sub, accent, danger, big }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: big ? '14px 16px' : '11px 13px' }}>
    <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: big ? 26 : 18, fontWeight: 700, color: danger ? '#dc2626' : (accent || '#111827') }}>{value}</div>
    {sub && <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 3 }}>{sub}</div>}
  </div>
);

/* ── helpers ── */
function cleanAudience(a = {}) {
  const out = {};
  if (a.listId) out.listId = Number(a.listId);
  if (a.status) out.status = a.status;
  if (a.source) out.source = a.source;
  if (a.tag) out.tag = a.tag.trim();
  return out;
}
const inputStyle  = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, color: '#111827', outline: 'none', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, width: 'auto', minWidth: 130, background: '#fff' };
const primaryBtn  = { padding: '9px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const ghostBtn    = { padding: '9px 14px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const miniBtn     = { padding: '5px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const toggleBtn   = (active) => ({ padding: '5px 12px', background: active ? '#eff6ff' : '#fff', color: active ? '#2563eb' : '#6b7280', border: `1px solid ${active ? '#bfdbfe' : '#d1d5db'}`, borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' });
const Label = ({ children, style }) => <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, ...style }}>{children}</div>;
const Th = ({ children }) => <th style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{children}</th>;
const Td = ({ children }) => <td style={{ padding: '11px 14px', color: '#111827' }}>{children}</td>;
const Card = ({ title, children }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 16 }}>{title}</div>
    {children}
  </div>
);
const Banner = ({ msg }) => (
  <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
    background: msg.ok ? '#ecfdf5' : '#fef2f2', color: msg.ok ? '#059669' : '#dc2626',
    border: `1px solid ${msg.ok ? '#a7f3d0' : '#fecaca'}` }}>{msg.text}</div>
);

/* Full email preview (how it lands in the inbox), desktop/mobile. */
const PreviewModal = ({ subject, fromName, fromEmail, html, onClose }) => {
  const [device, setDevice] = React.useState('desktop');
  const w = device === 'desktop' ? 600 : 360;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.6)', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1100, padding: 16, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 820, marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>Preview</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setDevice('desktop')} style={previewTab(device === 'desktop')}>🖥 Desktop</button>
            <button onClick={() => setDevice('mobile')} style={previewTab(device === 'mobile')}>📱 Mobile</button>
            <button onClick={onClose} style={{ ...previewTab(false), fontWeight: 700 }}>✕ Close</button>
          </div>
        </div>
        <div style={{ background: '#f1f5f9', borderRadius: 12, padding: 16, display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: w, maxWidth: '100%', background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 6px 24px rgba(0,0,0,0.18)' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{subject || '(no subject)'}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{fromName}{fromEmail ? ` <${fromEmail}>` : ''}</div>
            </div>
            <div style={{ padding: 16 }} dangerouslySetInnerHTML={{ __html: html || '<p style="color:#9ca3af">Nothing to preview yet.</p>' }} />
          </div>
        </div>
      </div>
    </div>
  );
};
const previewTab = (active) => ({ padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid ' + (active ? '#fff' : 'rgba(255,255,255,0.4)'), background: active ? '#fff' : 'transparent', color: active ? '#111827' : '#fff' });

/* In-app confirmation dialog (replaces the native window.confirm). */
const ConfirmModal = ({ title, message, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) => (
  <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
      <div style={{ padding: '22px 24px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: danger ? '#fef2f2' : '#eff6ff', color: danger ? '#dc2626' : '#2563eb', fontSize: 18 }}>
            <i className={danger ? 'fas fa-triangle-exclamation' : 'fas fa-paper-plane'} />
          </div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>{title}</div>
        </div>
        <div style={{ fontSize: 14.5, color: '#4b5563', lineHeight: 1.55 }}>{message}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '0 24px 22px' }}>
        <button onClick={onCancel} style={ghostBtn}>Cancel</button>
        <button onClick={onConfirm} style={{ ...primaryBtn, background: danger ? '#dc2626' : '#2563eb' }}>{confirmLabel}</button>
      </div>
    </div>
  </div>
);
